import { CircularProgress, Drawer, Stack } from '@mui/material'
import type { FC } from 'react'
import { useQueryLabel } from 'src/features/labelMaster/api/useQueryLabel'
import { DrawerHeader } from './DrawerHeader'
import { AreaEditView } from './LabelForm/AreaEditView'
import {
  LabelFormProvider,
  useLabelFormContext,
} from './LabelForm/LabelFormContext'
import { LabelOverview } from './LabelForm/LabelOverview'
import { useDrawerAnimation } from './useDrawerAnimation'

type Props = {
  labelMasterId: string
  labelId: string
  onClose: () => void
}

export const LabelEditDrawer: FC<Props> = ({
  labelMasterId,
  labelId,
  onClose,
}) => {
  const { data: label, isLoading } = useQueryLabel(labelId)
  const { open, requestClose, handleExited } = useDrawerAnimation({
    onExited: onClose,
  })

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={requestClose}
      slotProps={{
        transition: {
          onExited: handleExited,
        },
        paper: {
          sx: { height: '100vh', width: '100%' },
        },
      }}
    >
      {isLoading || !label ? (
        <>
          <DrawerHeader
            onClose={requestClose}
            canSubmit={false}
            onSave={() => {}}
          />
          <Stack
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
          >
            <CircularProgress />
          </Stack>
        </>
      ) : (
        <LabelFormProvider
          label={label}
          labelMasterId={labelMasterId}
          onClose={onClose}
        >
          <LabelEditContent />
        </LabelFormProvider>
      )}
    </Drawer>
  )
}

const LabelEditContent: FC = () => {
  const { editingTarget, canSubmit, handleSave, handleClose } =
    useLabelFormContext()

  const isEditingArea =
    editingTarget.type === 'area-new' || editingTarget.type === 'area-edit'

  return (
    <>
      <DrawerHeader
        onClose={handleClose}
        canSubmit={canSubmit}
        onSave={handleSave}
      />
      <Stack bgcolor="grey.100" height="100%">
        {isEditingArea ? (
          <AreaEditView
            editingArea={
              editingTarget.type === 'area-edit'
                ? editingTarget.area
                : undefined
            }
          />
        ) : (
          <LabelOverview />
        )}
      </Stack>
    </>
  )
}
